import clsx from "clsx";
import React, { useMemo } from "react";
// Direct sibling imports (not the "@appsmith/wds" barrel) to avoid adding a
// new package-barrel cyclic dependency — see ci-client-cyclic-deps-check.
import { FieldError } from "../../FieldError";
import { FieldLabel } from "../../FieldLabel";
import { Icon } from "../../Icon";
import { IconButton } from "../../IconButton";
import { Input } from "../../Input";
import { inputFieldStyles } from "../../Field";
import { SearchField as HeadlessSearchField } from "react-aria-components";

import styles from "./styles.module.css";
import type { SearchFieldProps } from "./types";

const searchIcon = <Icon name="search" />;

/**
 * Thin WDS layer over react-aria-components' SearchField, mirroring the
 * TextField layering (HeadlessField → FieldLabel → Input → FieldError).
 *
 * RAC supplies the search semantics for free: the inner input renders with
 * `type="search"` (role searchbox), Escape clears, and the suffix button is
 * slotted into RAC's clear-button context (press clears the value, fires
 * `onClear` + `onChange("")`, and refocuses the input).
 */
export function SearchField(props: SearchFieldProps) {
  const {
    className,
    contextualHelp,
    errorMessage,
    isDisabled,
    isInvalid,
    isLoading,
    isReadOnly,
    isRequired,
    label,
    placeholder,
    prefix,
    size = "medium",
    value,
    ...rest
  } = props;

  const clearButton = useMemo(
    () => (
      <IconButton
        color="neutral"
        data-search-clear-button=""
        excludeFromTabOrder
        icon="x"
        size={size === "medium" ? "small" : "xSmall"}
        variant="ghost"
      />
    ),
    [size],
  );

  return (
    <HeadlessSearchField
      aria-label={Boolean(label) ? undefined : "Search"}
      {...rest}
      className={clsx(inputFieldStyles.field, styles.searchField, className)}
      data-field=""
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      value={value}
    >
      <FieldLabel
        contextualHelp={contextualHelp}
        isDisabled={isDisabled}
        isRequired={isRequired}
      >
        {label}
      </FieldLabel>
      <Input
        data-input
        isLoading={isLoading}
        isReadOnly={isReadOnly}
        placeholder={placeholder}
        prefix={prefix ?? searchIcon}
        size={size}
        suffix={clearButton}
        value={value}
      />
      <FieldError>{errorMessage}</FieldError>
    </HeadlessSearchField>
  );
}
